import { api } from '@/config/api';

export interface MetaInstagramAccount {
  id: string;
  username?: string;
  profilePictureUrl?: string;
}

export interface MetaPage {
  id: string;
  name: string;
  pictureUrl?: string;
  instagramAccount: MetaInstagramAccount | null;
}

export interface WaNumber {
  accountUid: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  verifiedName: string;
}

export interface MetaPixel {
  id: string;
  name: string;
  /** ISO timestamp from Meta — pixels with no recent fire may be orphaned. */
  lastFiredTime?: string;
  creationTime?: string;
}

export interface MetaApp {
  id: string;
  name: string;
  link?: string;
  iconUrl?: string;
  namespace?: string;
  /** Per-platform store URLs Meta has on file. Presence of `iphone_app_store`
   *  or `google_play` means this app is registered as a mobile app and is
   *  eligible for App Promotion ad sets. */
  objectStoreUrls?: {
    iphone_app_store?: string;
    google_play?: string;
    windows_phone_store?: string;
    windows_10_store?: string;
    amazon_app_store?: string;
  };
  /** True for apps the user added manually. Manual apps can be deleted from
   *  the picker; remote (Meta-fetched) apps cannot. */
  isManual?: boolean;
}

export interface SaveMetaAppInput {
  appId: string;
  name: string;
  googlePlayUrl?: string;
  iphoneAppStoreUrl?: string;
  amazonAppStoreUrl?: string;
}

export const metaResourcesService = {
  pages: async (): Promise<MetaPage[]> =>
    (await api.get('/slighter_crm/facebook/meta-resources/pages')).data,
  /**
   * Returns every IG business account the user manages, deduped by id.
   * Independent of the FB Page selection — Meta lets you pair any Page with
   * any IG you have rights over via `instagram_actor_id`.
   */
  instagramAccounts: async (): Promise<MetaInstagramAccount[]> =>
    (await api.get('/slighter_crm/facebook/meta-resources/instagram-accounts')).data,
  whatsappNumbers: async (): Promise<WaNumber[]> =>
    (await api.get('/slighter_crm/facebook/meta-resources/whatsapp-numbers')).data,
  pixels: async (): Promise<MetaPixel[]> =>
    (await api.get('/slighter_crm/facebook/meta-resources/pixels')).data,
  createPixel: async (name: string): Promise<MetaPixel> =>
    (await api.post('/slighter_crm/facebook/meta-resources/pixels', { name })).data,
  apps: async (): Promise<MetaApp[]> =>
    (await api.get('/slighter_crm/facebook/meta-resources/apps')).data,
  saveApp: async (input: SaveMetaAppInput): Promise<MetaApp> =>
    (await api.post('/slighter_crm/facebook/meta-resources/apps', input)).data,
  deleteApp: async (appId: string): Promise<void> => {
    await api.delete(`/slighter_crm/facebook/meta-resources/apps/${appId}`);
  },
};
